import React, { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import { ButtonBar, ScreenHeader } from '../../components';
import { useScrollToTop } from '../../hooks';

import { NewPOAContext } from './new-poa-composer';

export function NewPOAPDF() {
  const { powerOfAttorney } = useContext(NewPOAContext);
  const navigate = useNavigate();
  
  useScrollToTop();
  
  const handleCancel = useCallback(() => {
    navigate('/home');
  }, [ navigate ]);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const handleSave = useCallback(() => {
    // Implement save functionality
    console.log('Saving PDF...');
  }, []);

  const handleChangeAnswers = useCallback(() => {
    navigate('summary');
  }, [ navigate ]);

  const handleContinue = useCallback(() => {
    navigate('success');
  }, [ navigate ]);
  
  return (
    <div className="new-poa-pdf screen-container">
      <ScreenHeader
        title="Your Power of Attorney Document"
        description="Your document has been generated. You can print, save, or make changes as needed."
      />
      
      <div className="pdf-container">
        <div className="pdf-actions">
          <button onClick={handlePrint} className="action-button print-button">
            Print Document
          </button>
          <button onClick={handleSave} className="action-button save-button">
            Save PDF
          </button>
          <button onClick={handleChangeAnswers} className="action-button edit-button">
            Change Answers
          </button>
        </div>

        <div className="pdf-preview">
          <div className="document-header">
            <h1>DURABLE POWER OF ATTORNEY FOR FINANCES</h1>
            <p className="document-subtitle">
              State of {powerOfAttorney.state_of_residence}
            </p>
          </div>

          <div className="document-content">
            <p>
              I, {powerOfAttorney.full_name}, of {powerOfAttorney.address}, {powerOfAttorney.city}, {powerOfAttorney.state} {powerOfAttorney.zip}, 
              hereby appoint the following person(s) as my agent(s) to act for me in any lawful way 
              with respect to the following powers:
            </p>

            <div className="powers-section">
              <h3>POWERS GRANTED</h3>
              <ul>
                {powerOfAttorney.real_estate_power !== 'no' && (
                  <li>Real Estate Transactions {powerOfAttorney.real_estate_power === 'yes_no_sell_home' ? '(except sale of primary residence)' : ''}</li>
                )}
                {powerOfAttorney.tangible_personal_property && <li>Tangible Personal Property Transactions</li>}
                {powerOfAttorney.securities_transactions && <li>Securities Transactions</li>}
                {powerOfAttorney.banking_transactions && <li>Banking Transactions</li>}
                {powerOfAttorney.operating_business && <li>Operating a Business</li>}
                {powerOfAttorney.digital_assets && <li>Digital Assets</li>}
                {powerOfAttorney.insurance_annuity && <li>Insurance and Annuity Transactions</li>}
                {powerOfAttorney.estate_trust_transactions && <li>Estate and Trust Transactions</li>}
                {powerOfAttorney.legal_actions && <li>Legal Actions</li>}
                {powerOfAttorney.personal_family_care && <li>Personal and Family Care</li>}
                {powerOfAttorney.pet_care && <li>Pet Care</li>}
                {powerOfAttorney.government_benefits && <li>Government Benefits</li>}
                {powerOfAttorney.retirement_plan_transactions && <li>Retirement Plan Transactions</li>}
                {powerOfAttorney.tax_matters && <li>Tax Matters</li>}
              </ul>
            </div>

            <div className="agents-section">
              <h3>AGENTS APPOINTED</h3>
              {powerOfAttorney.agents?.map((agent, index) => (
                <div key={index} className="agent-info">
                  <p><strong>Agent {index + 1}:</strong> {agent.name}</p>
                  <p><strong>Address:</strong> {agent.address}, {agent.city}, {agent.state} {agent.zip}</p>
                  <p><strong>Phone:</strong> {agent.phone}</p>
                  {agent.email && <p><strong>Email:</strong> {agent.email}</p>}
                </div>
              ))}
            </div>

            {powerOfAttorney.alternate_agents?.length > 0 && (
              <div className="alternate-agents-section">
                <h3>ALTERNATE AGENTS</h3>
                {powerOfAttorney.alternate_agents.map((agent, index) => (
                  <div key={index} className="agent-info">
                    <p><strong>Alternate Agent {index + 1}:</strong> {agent.name}</p>
                    <p><strong>Address:</strong> {agent.address}, {agent.city}, {agent.state} {agent.zip}</p>
                    <p><strong>Phone:</strong> {agent.phone}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="signature-section">
              <p>This Power of Attorney is effective immediately and shall continue until revoked.</p>
              <p>Dated: _______________</p>
              <p>Signature: _______________</p>
              <p>Print Name: {powerOfAttorney.full_name}</p>
            </div>
          </div>
        </div>
      </div>

      <ButtonBar 
        handlePrimary={handleContinue}
        handleSecondary={handleCancel}
        primaryText="Continue"
        secondaryText="Cancel"
      />
    </div>
  );
}
